import {SentimentType} from "../../definitions/enums";
import {CommentType, Nullable, VariantAttributeType} from "../../definitions/types";
import {ReviewMediaReviewRatingModel} from "./reviewMediaReviewRatingModel";

export class ReviewMediaReviewModel {
    private readonly _id: number;
    private readonly _ratingId: number;
    private readonly _title?: string;
    private readonly _text: string;
    private readonly _sentiment?: SentimentType;
    private readonly _helpfulCount: number;
    private readonly _visible: boolean;
    private readonly _created: Date;
    private readonly _rating: ReviewMediaReviewRatingModel;

    constructor(reviewMediaType: {
        id: number
        ratingId: number
        title?: string
        text: string
        sentiment?: SentimentType
        helpfulCount: number
        visible: boolean
        created: Date
        rating: {
            id: number
            orderNumber: string
            productId: number
            primaryId?: number
            grade: number
            accountId?: number
            name: string
            visible: boolean
            created: Date
            updated?: Date
            comments: Nullable<CommentType>[]
            attributes: Nullable<VariantAttributeType>[]
        }
    }) {
        this._id = reviewMediaType.id;
        this._ratingId = reviewMediaType.ratingId;
        this._title = reviewMediaType.title;
        this._text = reviewMediaType.text;
        this._sentiment = reviewMediaType.sentiment;
        this._helpfulCount = reviewMediaType.helpfulCount;
        this._visible = reviewMediaType.visible;
        this._created = reviewMediaType.created;
        this._rating = new ReviewMediaReviewRatingModel(reviewMediaType.rating);
    }

    /**
     * @property id
     * @description Returns the id of the review.
     * @returns {number}
     */
    public get id(): number {
        return this._id;
    }

    /**
     * @property ratingId
     * @description Returns the rating id of the review.
     * @returns {number}
     */
    public get ratingId(): number {
        return this._ratingId;
    }

    /**
     * @property title
     * @description Returns the title of the review.
     * @returns {string | undefined}
     */
    public get title(): string | undefined {
        return this._title;
    }

    /**
     * @property text
     * @description Returns the text of the review.
     * @returns {string}
     */
    public get text(): string {
        return this._text;
    }

    /**
     * @property sentiment
     * @description Returns the sentiment of the review.
     * @returns {SentimentType | undefined}
     */
    public get sentiment(): SentimentType | undefined {
        return this._sentiment;
    }

    /**
     * @property helpfulCount
     * @description Returns the helpful count of the review.
     * @returns {number}
     */
    public get helpfulCount(): number {
        return this._helpfulCount;
    }

    /**
     * @property visible
     * @description Returns the visibility of the review.
     * @returns {boolean}
     */
    public get visible(): boolean {
        return this._visible;
    }

    /**
     * @property created
     * @description Returns the created date of the review in UTC.
     * @returns {Date}
     */
    public get created(): Date {
        return this._created;
    }

    /**
     * @property rating
     * @description Returns the rating of the review.
     * @returns {ReviewMediaReviewRatingModel}
     */
    public get rating(): ReviewMediaReviewRatingModel {
        return this._rating;
    }
}